/**
 * TopInstalledApps — «Aplicaciones más instaladas» de la consola de software.
 *
 * Mismo criterio que el resto de paneles: primero el dato que se opera (qué
 * software está en cuántos equipos y con qué salud), luego el detalle. La barra
 * compara instalaciones correctas contra el total de despliegues del paquete,
 * así un paquete con muchos errores se ve a simple vista aunque esté muy
 * extendido.
 *
 * Los datos vienen de `overview.by_package` (una sola llamada, sin recorrer
 * despliegues).
 */

import React, { useMemo } from 'react';

const PALETA = ['#2563eb', '#16a34a', '#f97316', '#8b5cf6', '#0891b2', '#dc2626'];

function iniciales(nombre) {
  const limpio = String(nombre || '?').replace(/[^A-Za-z0-9 ]/g, ' ').trim();
  const partes = limpio.split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

export default function TopInstalledApps({ byPackage = [], hostsCount = 0, onDeploy }) {
  const filas = useMemo(() => {
    const lista = Object.values(byPackage || {});
    return lista
      .filter((p) => (Number(p.done) || 0) > 0 || (Number(p.running) || 0) > 0)
      .sort((a, b) => (Number(b.done) || 0) - (Number(a.done) || 0))
      .slice(0, 8);
  }, [byPackage]);

  if (filas.length === 0) return null;

  const referencia = Math.max(hostsCount || 0, ...filas.map((p) => Number(p.done) || 0), 1);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 14,
        background: 'var(--bg-card)',
        padding: '14px 16px',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Aplicaciones más instaladas</h3>
        <span className="sf2-hint" style={{ margin: 0 }}>
          Instalaciones correctas sobre el total de despliegues del paquete
        </span>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {filas.map((p, idx) => {
          const done = Number(p.done) || 0;
          const total = Number(p.total) || done;
          const pct = referencia ? Math.round((done / referencia) * 100) : 0;
          const color = PALETA[idx % PALETA.length];
          return (
            <div key={p.package_id} style={{ display: 'grid', gridTemplateColumns: '28px 170px 1fr 96px auto', gap: 10, alignItems: 'center' }}>
              <span
                aria-hidden="true"
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: color,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {iniciales(p.name)}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>
                {p.name}
              </span>
              <span style={{ height: 8, borderRadius: 6, background: 'var(--border)', overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: color }} />
              </span>
              <span style={{ fontSize: 12, textAlign: 'right', color: 'var(--text-secondary)' }}>
                {done}/{total} · {pct}%
              </span>
              {onDeploy ? (
                <button
                  type="button"
                  className="list-btn-secondary"
                  title={`Desplegar ${p.name}`}
                  onClick={() => onDeploy(p)}
                >
                  Instalar
                </button>
              ) : <span />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
